import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Shipment, ShipmentEntry, ShipmentsState } from '../../types/shipment';

const SHIPMENTS_STORAGE_KEY = 'ingredient-shipments';

/**
 * Loads shipments from localStorage
 */
function loadShipmentsFromStorage(): Shipment[] {
  console.log('[Shipments] Loading shipments from localStorage');

  try {
    const stored = localStorage.getItem(SHIPMENTS_STORAGE_KEY);
    if (!stored) {
      console.log('[Shipments] No data found');
      return [];
    }

    const parsed = JSON.parse(stored);

    if (!Array.isArray(parsed)) {
      console.warn('[Shipments] Data is not an array');
      return [];
    }

    console.log(`[Shipments] ✓ Loaded ${parsed.length} shipments`);
    return parsed;
  } catch (error) {
    console.error('[Shipments] Failed to load from storage:', error);
    return [];
  }
}

/**
 * Saves shipments to localStorage
 */
function saveShipmentsToStorage(shipments: Shipment[]): void {
  try {
    localStorage.setItem(SHIPMENTS_STORAGE_KEY, JSON.stringify(shipments));
    console.log(`[Shipments] ✓ Saved ${shipments.length} shipments to localStorage`);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('[Shipments] localStorage quota exceeded!');
      alert('Storage limit exceeded. Please remove some shipments to continue.');
    } else {
      console.error('[Shipments] Failed to save to storage:', error);
    }
  }
}

// Initial state
const initialState: ShipmentsState = {
  shipments: loadShipmentsFromStorage()
};

// Helper function to create a blank entry
export function createBlankEntry(): ShipmentEntry {
  return {
    id: uuidv4(),
    supplierName: '',
    supplierId: '',
    ingredient: '',
    lotNumber: '',
    quantity: 0,
    unit: 'lbs',
    conditionUponArrival: '',
    receivingTemperature: 0,
    temperatureUnit: 'F',
    receivedBy: '',
    correctiveAction: '',
    storageType: 'non_refrigerated',
    createdAt: new Date().toISOString()
  };
}

// Helper function to create a blank shipment
export function createBlankShipment(): Shipment {
  const today = new Date();
  const dateString = today.toISOString().split('T')[0]; // YYYY-MM-DD

  return {
    id: uuidv4(),
    shipmentDate: dateString,
    entries: [createBlankEntry()],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

const shipmentsSlice = createSlice({
  name: 'shipments',
  initialState,
  reducers: {
    addShipment: (state, action: PayloadAction<Shipment>) => {
      state.shipments.push(action.payload);
      saveShipmentsToStorage(state.shipments);
    },

    updateShipment: (state, action: PayloadAction<Shipment>) => {
      const index = state.shipments.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.shipments[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
        saveShipmentsToStorage(state.shipments);
      }
    },

    deleteShipment: (state, action: PayloadAction<string>) => {
      state.shipments = state.shipments.filter(s => s.id !== action.payload);
      saveShipmentsToStorage(state.shipments);
    },

    addEntryToShipment: (state, action: PayloadAction<{ shipmentId: string; entry: ShipmentEntry }>) => {
      const shipment = state.shipments.find(s => s.id === action.payload.shipmentId);
      if (shipment) {
        shipment.entries.push(action.payload.entry);
        shipment.updatedAt = new Date().toISOString();
        saveShipmentsToStorage(state.shipments);
      }
    },

    updateEntry: (state, action: PayloadAction<{ shipmentId: string; entry: ShipmentEntry }>) => {
      const shipment = state.shipments.find(s => s.id === action.payload.shipmentId);
      if (shipment) {
        const entryIndex = shipment.entries.findIndex(e => e.id === action.payload.entry.id);
        if (entryIndex !== -1) {
          shipment.entries[entryIndex] = action.payload.entry;
          shipment.updatedAt = new Date().toISOString();
          saveShipmentsToStorage(state.shipments);
        }
      }
    },

    deleteEntry: (state, action: PayloadAction<{ shipmentId: string; entryId: string }>) => {
      const shipment = state.shipments.find(s => s.id === action.payload.shipmentId);
      if (shipment && shipment.entries.length > 1) {
        shipment.entries = shipment.entries.filter(e => e.id !== action.payload.entryId);
        shipment.updatedAt = new Date().toISOString();
        saveShipmentsToStorage(state.shipments);
      }
    }
  }
});

// eslint-disable-next-line prettier/prettier
export const { addShipment, updateShipment, deleteShipment, addEntryToShipment, updateEntry, deleteEntry } = shipmentsSlice.actions;

export default shipmentsSlice.reducer;
